import type { ApiClient } from '../../../shared/api/api-client';
import type {
  PaymentGatewayAuditDashboardStats,
  PaymentGatewayAuditEntry,
  PaymentGatewayAuditTotals,
  PgaListQuery
} from './payment-gateway-audit.types';

const PGA_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Даты только `YYYY-MM-DD` на бэкенде парсятся как полночь UTC (`created_to` тогда обрезала бы весь день).
 * Приводим к границам суток UTC, чтобы фильтр по дате из UI был включительным.
 */
export function normalizePgaCreatedQueryValue(
  raw: string | null | undefined,
  bound: 'from' | 'to'
): string | null {
  const s = (raw ?? '').trim();
  if (!s) return null;
  if (PGA_DATE_ONLY.test(s)) {
    return bound === 'from' ? `${s}T00:00:00.000Z` : `${s}T23:59:59.999Z`;
  }
  return s;
}

function appendQueryParams(sp: URLSearchParams, q: PgaListQuery): void {
  if (q.limit != null && q.limit > 0) sp.set('limit', String(q.limit));
  if (q.offset != null && q.offset >= 0) sp.set('offset', String(q.offset));
  if (q.gateway != null && String(q.gateway).trim() !== '') sp.set('gateway', String(q.gateway).trim());
  if (q.direction != null && String(q.direction).trim() !== '') sp.set('direction', String(q.direction).trim());
  const fromNorm = normalizePgaCreatedQueryValue(q.created_from ?? null, 'from');
  if (fromNorm) sp.set('created_from', fromNorm);
  const toNorm = normalizePgaCreatedQueryValue(q.created_to ?? null, 'to');
  if (toNorm) sp.set('created_to', toNorm);
  if (q.organization_id != null && q.organization_id > 0) {
    sp.set('organization_id', String(q.organization_id));
  }
  if (q.client_id != null && q.client_id > 0) sp.set('client_id', String(q.client_id));
  if (q.branch_id != null && q.branch_id > 0) sp.set('branch_id', String(q.branch_id));
}

function unwrapItems<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

function totalFromBody(data: unknown, headerTotal: number): number {
  if (headerTotal > 0) return headerTotal;
  if (data && typeof data === 'object' && typeof (data as { total?: unknown }).total === 'number') {
    const t = (data as { total: number }).total;
    return Number.isFinite(t) && t >= 0 ? t : 0;
  }
  return 0;
}

/** Tenant / СА в org-context: `GET /admin/payment-gateway-audit`. */
export async function fetchPaymentGatewayAuditListAdmin(
  client: ApiClient,
  q: PgaListQuery
): Promise<{ items: PaymentGatewayAuditEntry[]; total: number }> {
  const sp = new URLSearchParams();
  appendQueryParams(sp, q);
  const path = `/admin/payment-gateway-audit${sp.toString() ? `?${sp.toString()}` : ''}`;
  const { data, total: hdr } = await client.requestJsonWithTotalCount<unknown>(path, { method: 'GET' });
  const items = unwrapItems<PaymentGatewayAuditEntry>(data);
  return { items, total: totalFromBody(data, hdr) };
}

/** СА без org-context: `GET /superadmin/payment-gateway-audit`. */
export async function fetchPaymentGatewayAuditListSuperadmin(
  client: ApiClient,
  q: PgaListQuery
): Promise<{ items: PaymentGatewayAuditEntry[]; total: number }> {
  const sp = new URLSearchParams();
  appendQueryParams(sp, q);
  const path = `/superadmin/payment-gateway-audit${sp.toString() ? `?${sp.toString()}` : ''}`;
  const { data, total: hdr } = await client.requestJsonWithTotalCount<unknown>(path, { method: 'GET' });
  const items = unwrapItems<PaymentGatewayAuditEntry>(data);
  return { items, total: totalFromBody(data, hdr) };
}

/** Только tenant / org-context. */
export async function fetchPaymentGatewayAuditStats(
  client: ApiClient
): Promise<PaymentGatewayAuditDashboardStats> {
  return client.request<PaymentGatewayAuditDashboardStats>('/admin/payment-gateway-audit/stats', { method: 'GET' });
}

export async function fetchPaymentGatewayAuditTotalsAdmin(
  client: ApiClient,
  q: Pick<PgaListQuery, 'gateway' | 'direction' | 'created_from' | 'created_to'>
): Promise<PaymentGatewayAuditTotals> {
  const sp = new URLSearchParams();
  appendQueryParams(sp, {
    gateway: q.gateway,
    direction: q.direction,
    created_from: q.created_from,
    created_to: q.created_to
  });
  const path = `/admin/payment-gateway-audit/totals${sp.toString() ? `?${sp.toString()}` : ''}`;
  return client.request<PaymentGatewayAuditTotals>(path, { method: 'GET' });
}

export async function fetchPaymentGatewayAuditTotalsSuperadmin(
  client: ApiClient,
  q: PgaListQuery
): Promise<PaymentGatewayAuditTotals> {
  const sp = new URLSearchParams();
  appendQueryParams(sp, q);
  const path = `/superadmin/payment-gateway-audit/totals${sp.toString() ? `?${sp.toString()}` : ''}`;
  return client.request<PaymentGatewayAuditTotals>(path, { method: 'GET' });
}
